<template>
  <div class="orangehrm-kpi-summary-group">
    <div class="orangehrm-kpi-summary">
      <div class="orangehrm-kpi-summary__header">
        <oxd-text tag="h6" class="orangehrm-kpi-summary__title">
          {{ $t('performance.kpi_summary') }}
        </oxd-text>
      </div>

      <div
        v-for="summary in mergedData"
        :key="summary.categoryId"
        class="orangehrm-kpi-summary__category"
      >
        <div class="orangehrm-kpi-summary__category-head">
          <div class="orangehrm-kpi-summary__category-name">
            {{ summary.categoryName }}
          </div>
          <div class="orangehrm-kpi-summary__category-meta">
            <span class="orangehrm-kpi-summary__pill">
              {{ $t('performance.kpi_weight') }}: {{ summary.weight }}%
            </span>
            <span
              class="orangehrm-kpi-summary__pill orangehrm-kpi-summary__pill--muted"
            >
              {{ $t('performance.kpi_total_rating') }}:
              {{ formatSummaryNumber(summary.totalKpi) }}
            </span>
          </div>
        </div>

        <div class="orangehrm-kpi-summary__metrics">
          <div class="orangehrm-kpi-summary__metric">
            <span class="orangehrm-kpi-summary__metric-label">
              {{ $t('performance.kpi_total_value_rating') }}
            </span>
            <span class="orangehrm-kpi-summary__metric-value">
              {{ formatSummaryNumber(summary.totalValue) }}%
            </span>
          </div>
          <div class="orangehrm-kpi-summary__metric">
            <span class="orangehrm-kpi-summary__metric-label">
              {{ $t('performance.kpi_total_value_x_rating') }}
            </span>
            <span class="orangehrm-kpi-summary__metric-value">
              {{ formatSummaryNumber(summary.totalValueTimesWeight) }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="mergedData.length > 0" class="orangehrm-kpi-summary__total">
        <div class="orangehrm-kpi-summary__total-row">
          <div class="orangehrm-kpi-summary__total-label">
            {{ $t('performance.kpi_summary') }}
          </div>
          <div class="orangehrm-kpi-summary__total-value">{{ grandTotal }}</div>
        </div>
      </div>
    </div>

    <div
      v-if="mergedData.length > 0 && showPerformanceRating"
      class="orangehrm-performance-result"
    >
      <oxd-text tag="h6" class="orangehrm-performance-result__title">
        {{ $t('performance.rating') }}
      </oxd-text>
      <div class="orangehrm-performance-result__value" :class="scoreValueClass">
        {{ displayScoreLabel }}
      </div>
    </div>
  </div>
</template>

<script>
import {
  summarizePerformanceScore,
  getPerformanceScoreTierClass,
  formatPerformanceScoreLabel,
} from '@/orangehrmPerformancePlugin/util/performanceScore';

export default {
  name: 'KpiCalculationSummary',

  props: {
    mergedData: {type: Array, default: () => []},
  },

  computed: {
    grandTotal() {
      if (!Array.isArray(this.mergedData) || this.mergedData.length === 0) {
        return '0';
      }

      const total = this.mergedData.reduce((sum, category) => {
        const value = parseFloat(category.totalValueTimesWeight);
        return sum + (Number.isFinite(value) ? value : 0);
      }, 0);

      return Number.isFinite(total) ? Number(total.toFixed(2)).toString() : '0';
    },

    showPerformanceRating() {
      const total = parseFloat(this.grandTotal);
      return Number.isFinite(total) && total > 0 && !!this.displayScoreLabel;
    },

    displayScoreLabel() {
      return formatPerformanceScoreLabel(
        summarizePerformanceScore(this.grandTotal),
      );
    },

    scoreValueClass() {
      return getPerformanceScoreTierClass(
        summarizePerformanceScore(this.grandTotal),
      ).replace(
        'orangehrm-performance-score-label--',
        'orangehrm-performance-result__value--',
      );
    },
  },

  methods: {
    formatSummaryNumber(value) {
      const num = parseFloat(value);
      return Number.isFinite(num) ? value : '0';
    },
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-kpi-summary-group {
  margin-top: 1.5rem;
}

.orangehrm-kpi-summary {
  border: 1px solid $oxd-interface-gray-lighten-2-color;
  border-radius: 1rem;
  overflow: hidden;
  background: $oxd-white-color;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);

  &__header {
    padding: 1rem 1.25rem;
    background: linear-gradient(
      135deg,
      rgba($oxd-primary-one-color, 0.12) 0%,
      rgba($oxd-primary-one-color, 0.04) 100%
    );
    border-bottom: 2px solid rgba($oxd-primary-one-color, 0.2);
  }

  &__title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: $oxd-interface-gray-darken-2-color;
  }

  &__category {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid $oxd-interface-gray-lighten-2-color;

    &:last-of-type {
      border-bottom: none;
    }
  }

  &__category-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.875rem;
  }

  &__category-name {
    font-size: 0.9375rem;
    font-weight: 700;
    color: $oxd-primary-one-color;
    line-height: 1.4;
  }

  &__category-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  &__pill {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.65rem;
    border-radius: 999px;
    background: rgba($oxd-primary-one-color, 0.1);
    color: $oxd-primary-one-color;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;

    &--muted {
      background: $oxd-interface-gray-lighten-2-color;
      color: $oxd-interface-gray-darken-1-color;
    }
  }

  &__metrics {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
  }

  &__metric {
    padding: 0.75rem 0.875rem;
    border-radius: 0.75rem;
    background: $oxd-interface-gray-lighten-2-color;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    min-width: 0;
  }

  &__metric-label {
    font-size: 0.75rem;
    color: $oxd-interface-gray-darken-1-color;
    line-height: 1.35;
  }

  &__metric-value {
    font-size: 1.125rem;
    font-weight: 700;
    color: $oxd-interface-gray-darken-2-color;
  }

  &__total {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    background: $oxd-interface-gray-lighten-1-color;
    border-top: 2px solid $oxd-primary-one-color;
  }

  &__total-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  &__total-label {
    font-size: 0.9375rem;
    font-weight: 700;
    color: $oxd-interface-gray-darken-2-color;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__total-value {
    font-size: 1.25rem;
    font-weight: 800;
    color: $oxd-primary-one-color;
    line-height: 1;
  }

  @media (max-width: 640px) {
    &__metrics {
      grid-template-columns: 1fr;
    }
  }
}

.orangehrm-performance-result {
  margin-top: 1rem;
  padding: 1rem 1.25rem;
  border: 1px solid $oxd-interface-gray-lighten-2-color;
  border-radius: 1rem;
  background: $oxd-white-color;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  @include oxd-respond-to('md') {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  &__title {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 700;
    color: $oxd-interface-gray-darken-2-color;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__value {
    font-size: 1.125rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    line-height: 1.2;

    @include oxd-respond-to('md') {
      text-align: right;
    }

    &--excellent {
      color: #047857;
    }

    &--good {
      color: #15803d;
    }

    &--average {
      color: #a16207;
    }

    &--improvement {
      color: #c2410c;
    }

    &--none {
      color: $oxd-interface-gray-darken-1-color;
    }
  }
}
</style>
